// React
import React, { useState } from 'react';
// Translation
import i18n from 'i18next';
// React Bootstrap
import { Accordion, Button, Form, InputGroup } from 'react-bootstrap';
// Font Awesome
import { faCircleInfo, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Components
import { Title } from '@fyrstain/fhir-front-library';
import { Fixture } from '../FixturesSection/FixturesSection';

/////////////////////////////
//          Props          //
/////////////////////////////

export interface AddTestScriptAssertProps {
    // Choose the test step between 'setup', 'test', 'teardown'
    testStep: string;
    asserts: [];
    // Operation id to generate the assert id
    operationId: string;
    // Pass the formData to the parent component
    setAssertFormData: React.Dispatch<React.SetStateAction<any>>;
    // List of selected profiles to populate the validateProfileId select options
    selectedProfiles: { internalId: string, name: string }[];
    // List of fixtures to populate the sourceId select options
    fixtures: Fixture[],
    // The type of the operation (REST operation or free operation)
    operationType: string;
    // Set the visibility of the operation form
    setShowOperationForm: React.Dispatch<React.SetStateAction<boolean>>;
    // The visibility of the operation form
    showOperationForm: boolean;
}

const AddTestScriptAssert: React.FC<AddTestScriptAssertProps> = (configs) => {

    /////////////////////////////
    //          Options        //
    /////////////////////////////

    // Options for the label of the assert
    const labelOptions = [
        { code: 'HTTPStatus', display: 'HTTP status' },
        { code: 'HTTPHeader', display: 'HTTP header' },
        { code: 'Format', display: 'Format' },
        { code: 'Resource', display: 'Resource' },
        { code: 'ValidateProfileId', display: 'Validate Profile id' },
        { code: 'FHIRPath', display: 'FHIR path' },
        { code: 'RequestURL', display: 'Request URL' },
        { code: 'MethodHTTP', display: 'Method HTTP' },
        { code: 'FreeAssert', display: 'Free assert' },
    ];

    // Options for the direction
    const directionOptions = [
        { code: 'request', display: 'Request' },
        { code: 'response', display: 'Response' },
    ];

    // Options for the operator
    const operatorOptions = [
        { code: 'equals', display: 'Equal' },
        { code: 'notEquals', display: 'Not equal' },
        { code: 'in', display: 'In' },
        { code: 'in', display: 'Not in' },
        { code: 'greaterThan', display: 'Greater than' },
        { code: 'lessThan', display: 'Less than' },
        { code: 'empty', display: 'Empty' },
        { code: 'notEmpty', display: 'Not empty' },
        { code: 'contains', display: 'Contains' },
        { code: 'notContains', display: 'Not contains' },
        { code: 'eval', display: 'Eval' },
        { code: 'manualEval', display: 'Manual eval' },
    ];

    // Options for the request method
    const requestMethodOptions = [
        { code: 'delete', display: 'DELETE' },
        { code: 'get', display: 'GET' },
        { code: 'options', display: 'OPTIONS' },
        { code: 'patch', display: 'PATCH' },
        { code: 'post', display: 'POST' },
        { code: 'put', display: 'PUT' },
        { code: 'head', display: 'HEAD' },
    ];

    /////////////////////////////
    //          States         //
    /////////////////////////////

    // Check if the edition form is validated before adding the operation
    const [editionValidated, setEditionValidated] = useState(false);

    // The form data to add an assert
    const [formData, setFormData] = useState({
        id: '' as string,
        description: '' as string,
        label: '' as string,
        direction: '' as string,
        expression: '' as string,
        responseCode: '' as string,
        validateProfileId: '' as string,
        headerField: '' as string,
        valueToCompare: '' as string,
        operator: '' as string,
        requestMethod: '' as string,
        requestUrl: '' as string,
        contentType: '' as string,
        resource: '' as string,
        sourceId: '' as string,
        stopTestOnFail: false as boolean,
        warningOnly: false as boolean
    });
    // The visibility of the form to add an assert
    const [showForm, setShowForm] = useState(false);
    // Messages for the required fields
    const [messages, setMessages] = useState<{ [key: string]: string }>({});
    // Store the original id while editing 
    const [currentAssertId, setCurrentAssertId] = useState<string | null>(null);

    /////////////////////////////
    //          Constants      //
    /////////////////////////////

    // The list of asserts
    const asserts = configs.asserts ?? [];

    // The common fields for each assert
    const defaultAssertFields = [
        'direction',
        'stopTestOnFail',
        'warningOnly'
    ];

    // The fields for each assert label
    const assertFieldsByLabel = {
        'HTTPStatus': [
            'responseCode',
            ...defaultAssertFields
        ],
        'HTTPHeader': [
            'headerField',
            'valueToCompare',
            'operator',
            ...defaultAssertFields
        ],
        'Format': [
            'contentType',
            ...defaultAssertFields
        ],
        'Resource': [
            'resource',
            ...defaultAssertFields
        ],
        'ValidateProfileId': [
            'validateProfileId',
            'expression',
            ...defaultAssertFields
        ],
        'FHIRPath': [
            'expression',
            'sourceId',
            ...defaultAssertFields
        ],
        'MethodHTTP': [
            'requestMethod',
            ...defaultAssertFields
        ],
        'RequestURL': [
            'requestUrl',
            ...defaultAssertFields
        ],
        'FreeAssert': [
            ...defaultAssertFields
        ]
    };

    // The required fields for each type of assert label
    const requiredFieldsByLabel = {
        default: [
            'description',
            'label',
        ],
        HTTPStatus: [
            'direction',
            'responseCode',
        ],
        HTTPHeader: [
            'direction',
            'headerField',
            'operator',
        ],
        Format: [
            'direction',
            'contentType',
        ],
        Resource: [
            'direction',
            'resource'
        ],
        ValidateProfileId: [
            'direction',
            'validateProfileId'
        ],
        FHIRPath: [
            'direction',
            'expression'
        ],
        MethodHTTP: [
            'direction',
            'requestMethod'
        ],
        RequestURL: [
            'direction',
            'requestUrl'
        ]
    };

    // Check if the direction field is required for FreeAssert
    const isDirectionRequired = !(configs.operationType === 'free-operation' && formData.label === 'FreeAssert');

    // Regex for the response code
    const responseCodeRegex = /^(100|101|20[0-6]|30[0-8]|40[0-7]|41[1-7]|421|422|426|50[0-5])([,|](100|101|20[0-6]|30[0-8]|40[0-7]|41[1-7]|421|422|426|50[0-5]))*([,|](100|101|20[0-6]|30[0-8]|40[0-7]|41[1-7]|421|422|426|50[0-5]))*$/;

    /////////////////////////////
    //          Tooltips       //
    /////////////////////////////

    // Translation for the stopTestOnFail tooltip
    const stopTestOnFail = i18n.t('tooltip.stoptestonfail');

    // Translation for the warningOnly tooltip
    const warningOnly = i18n.t('tooltip.warningonly');

    // Translation for the headerField tooltip
    const headerField = i18n.t('tooltip.headerfield');

    // Translation for the valueToCompare tooltip
    const valueToCompare = i18n.t('tooltip.valuetocompare');

    // Translation for the sourceId tooltip
    const sourceId = i18n.t('tooltip.resourceforfhirpathverification');

    // Translation for the fhirPath expression tooltip
    const fhirPathExpression = i18n.t('tooltip.fhirpathexpression');

    // Translation for the response code tooltip
    const responseCode = i18n.t('tooltip.responsecode');

    // Translation for the content type tooltip
    const contentType = i18n.t('tooltip.contenttype');

    /////////////////////////////
    //        Functions        //
    /////////////////////////////

    /**
     * Generate the assert id based on the operation id
     * 
     * @param testStep
     * @param index
     * 
     */
    const generateAssertId = (testStep: string, index: number): string => {
        return `${configs.operationId}-ASS-${String(index).padStart(index > 999 ? 4 : index > 99 ? 3 : 2, '0')}`;
    };

    /**
     * Check if response code is valid
     * 
     * @param responseCode 
     *  
     */
    const isValidResponseCode = (responseCode: string): boolean => {
        return responseCodeRegex.test(responseCode);
    };

    /**
     * Handle the change of the input fields
     *
     * @param e
     *
     */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, assert?: any) => {
        const { name, value } = e.target;
        if (name === 'label') {
            let defaultDirection = '';
            if (value === 'ValidateProfileId' || value === 'HTTPStatus') {
                defaultDirection = 'response';
            } else if (value === 'HTTPHeader' || value === 'RequestURL' || value === 'MethodHTTP') {
                defaultDirection = 'request';
            }
            setFormData(prevFormData => ({
                ...prevFormData,
                label: value,
                direction: defaultDirection,
                expression: '',
                responseCode: '',
                validateProfileId: '',
                headerField: '',
                valueToCompare: '',
                operator: '',
                requestMethod: '',
                requestUrl: '',
                contentType: '',
                resource: '',
                sourceId: '',
                stopTestOnFail: false,
                warningOnly: false
            }));
            setMessages({});
        }
        if (name === 'direction' && value === 'request') {
            setFormData(prevFormData => ({
                ...prevFormData,
                direction: value,
                responseCode: ''
            }));
            setMessages(prevMessages => {
                const newMessages = { ...prevMessages };
                delete newMessages.responseCode;
                return newMessages;
            });
        } else if (name === 'label') {
            setFormData(prevFormData => ({
                ...prevFormData,
                [name]: value,
            }));
            setMessages({});
        } else if (name === 'validateProfileId') {
            const selectedProfile = (configs.selectedProfiles ?? []).find(profile => profile.internalId === value);
            setFormData(prevFormData => ({
                ...prevFormData,
                validateProfileId: value,
                validateProfileName: selectedProfile?.name || '',
            }));
        } else if (name === 'sourceId') {
            const selectedFixture = configs.fixtures.find(fixture => fixture.internalId === value);
            setFormData(prevFormData => ({
                ...prevFormData,
                sourceId: selectedFixture?.internalId || '',
            }));
        } else {
            setFormData(prevFormData => ({
                ...prevFormData,
                [name]: value,
            }));
        }
        // Delete the error message when the field is filled
        if (value) {
            setMessages(prevMessages => {
                const newMessages = { ...prevMessages };
                delete newMessages[name];
                return newMessages;
            });
        }
    };

    /**
     * Handle the change of the response code input field
     * 
     * @param value 
     * @param assert 
     */
    const handleResponseCodeChange = (value: string, assert: any) => {
        handleChange({ target: { name: 'responseCode', value } } as React.ChangeEvent<HTMLInputElement>, assert);
        if (value && !isValidResponseCode(value)) {
            setMessages(prevMessages => ({
                ...prevMessages,
                responseCode: i18n.t('errormessage.invalidresponsecode')
            }));
        } else {
            setMessages(prevMessages => {
                const newMessages = { ...prevMessages };
                delete newMessages.responseCode;
                return newMessages;
            });
        }
    };

    /**
     * Handle the form to edit the assert from the edit icon
     * 
     * @param event 
     * 
     */
    const handleAssertEditingClick = (assert: any) => {
        setCurrentAssertId(assert.id);
        if (configs.showOperationForm) {
            const confirmCancel = window.confirm(i18n.t('confirmationmessage.canceloperation'));
            if (confirmCancel) {
                setShowForm(false);
            } else {
                return;
            }
        }
        if (showForm) {
            const confirmCancel = window.confirm(i18n.t('confirmationmessage.cancelassert'));
            if (!confirmCancel) {
                return;
            }
        }
        setFormData(assert);
        setShowForm(true);
        configs.setShowOperationForm(false);
    };

    /**
     * Delete an assert
     * 
     * @param assertId
     * 
     */
    const deleteAssert = (assertId: string) => {
        const updatedAsserts = asserts.filter((assert: { id: string; }) => assert.id !== assertId);
        const newAsserts = updatedAsserts.map((assert: any, index: any) => {
            return {
                ...assert,
                id: generateAssertId(configs.testStep, index + 1)
            };
        });
        configs.setAssertFormData((prevFormData: any) => ({
            ...prevFormData,
            [configs.operationId]: newAsserts
        }));
    };

    /**
     * Handle the delete of an assert from the faTrash icon to display a confirmation message
     * 
     * @param assertId
     */
    const handleDeleteAssert = (assertId: string) => {
        const confirmDelete = window.confirm(i18n.t('confirmationmessage.deleteassert'));
        if (confirmDelete) {
            deleteAssert(assertId);
        }
    };

    /**
     * Validate if the fields of the assert are filled before adding it in the accordion asserts section
     *
     * @param assert
     * 
     */
    const validateAssertFields = (assert: any) => {
        const requiredFields = requiredFieldsByLabel[assert.label as keyof typeof requiredFieldsByLabel] || [];
        const newMessages: { [key: string]: string } = {};
        let isValid = true;
        if (!assert.description) {
            newMessages['description'] = i18n.t('errormessage.requiredfield');
            isValid = false;
        }
        if (!assert.label) {
            newMessages['label'] = i18n.t('errormessage.requiredfield');
            isValid = false;
        }
        if (isDirectionRequired && !assert.direction) {
            newMessages['direction'] = i18n.t('errormessage.requiredfield');
            isValid = false;
        }
        requiredFields.forEach((field) => {
            if (field === 'responseCode') {
                if (!assert[field]) {
                    newMessages[field] = i18n.t('errormessage.requiredfield');
                    isValid = false;
                } else if (!isValidResponseCode(assert[field])) {
                    newMessages[field] = i18n.t('errormessage.invalidresponsecode');
                    isValid = false;
                } else {
                    delete newMessages[field];
                }
            } else if (!assert[field]) {
                newMessages[field] = i18n.t('errormessage.requiredfield');
                isValid = false;
            }
        });
        setMessages(newMessages);
        return isValid;
    };

    /**
     * Check if the field is required based on the label of the assert
     * 
     * @param field 
     * @param assert
     */
    const isFieldRequired = (field: string, assert: typeof formData) => {
        const requiredFields = requiredFieldsByLabel[assert.label as keyof typeof requiredFieldsByLabel] || requiredFieldsByLabel.default;
        return requiredFields.includes(field);
    };

    /**
     * Display the error message when the field is empty
     * 
     * @param field 
     *  
     */
    const displayMessage = (field: string) => {
        return messages && messages[field] ? (
            <InputGroup className="position-absolute text-danger fs-08">
                {messages[field]}
            </InputGroup>
        ) : null;
    }

    /**
     * Render the field based on the type of assert
     *
     * @param field
     * 
     */
    const renderField = (field: string, assert: typeof formData, isEditing: boolean) => {
        const fieldRequired = isFieldRequired(field, assert);
        // Fields
        switch (field) {
            case 'direction':
                return ((
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            Direction  {isEditing ? (isDirectionRequired ? '*' : '') : ':'} &nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <Form.Select
                                    required={isDirectionRequired}
                                    name='direction'
                                    value={
                                        formData.label === 'ValidateProfileId' || formData.label === 'HTTPStatus'
                                            ? 'response'
                                            : formData.label === 'HTTPHeader' || formData.label === 'RequestURL' || formData.label === 'MethodHTTP'
                                                ? 'request'
                                                : assert.direction
                                    }
                                    onChange={(e) => handleChange(e, assert)}
                                    disabled={
                                        formData.label === 'ValidateProfileId' ||
                                            formData.label === 'HTTPStatus' ||
                                            formData.label === 'HTTPHeader' ||
                                            formData.label === 'RequestURL' ||
                                            formData.label === 'MethodHTTP'
                                            ? true
                                            : false
                                    }
                                >
                                    <option
                                        className="text-muted"
                                        value=""
                                    >
                                        {i18n.t('placeholder.choosedirection')}
                                    </option>
                                    {directionOptions.map((option, index) => (
                                        option && (
                                            <option
                                                key={index}
                                                value={option.code}
                                            >
                                                {option.display}
                                            </option>
                                        )
                                    ))}
                                </Form.Select>
                                {formData.label !== 'ValidateProfileId' &&
                                    formData.label !== 'HTTPStatus' &&
                                    formData.label !== 'HTTPHeader' &&
                                    formData.label !== 'RequestURL'
                                    &&
                                    displayMessage('direction')
                                }
                            </>
                        ) : (
                            assert.direction !== '' && (
                                <Form.Text>{assert.direction}</Form.Text>
                            )
                        )}
                    </div>
                )
                );
            case 'expression':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.fhirpathexpression')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                            </Form.Label>
                            {!isEditing && assert.expression !== '' && (
                                <div>
                                    <Form.Text>{assert.expression}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={fhirPathExpression}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={fhirPathExpression}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="expression"
                                        value={assert.expression}
                                        onChange={(e) => handleChange(e, assert)}
                                    />
                                </InputGroup>
                                {displayMessage('expression')}
                            </>
                        )}
                    </div>
                );
            case 'responseCode':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.responsecode')} {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && assert.responseCode !== '' && (
                                <div>
                                    <Form.Text>{assert.responseCode}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={responseCode}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={responseCode}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="responseCode"
                                        value={assert.responseCode}
                                        onChange={(e) => handleResponseCodeChange(e.target.value, assert)}
                                        disabled={assert.direction === 'request'}
                                    />
                                </InputGroup>
                                {displayMessage('responseCode')}
                            </>
                        )}
                    </div>
                );
            case 'validateProfileId':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.validateprofileid')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <Form.Select
                                    required={fieldRequired}
                                    name="validateProfileId"
                                    value={assert.validateProfileId}
                                    onChange={(e) => handleChange(e, assert)}
                                >
                                    <option
                                        className="text-muted"
                                        value=""
                                    >
                                        {i18n.t('placeholder.choosevalidateprofile')}
                                    </option>
                                    {configs.selectedProfiles && configs.selectedProfiles.map(profile => (
                                        <option
                                            key={profile.internalId}
                                            value={profile.internalId}
                                        >
                                            {profile.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                {displayMessage('validateProfileId')}
                            </>
                        ) : (
                            <Form.Text>
                                {(() => {
                                    const index = configs.selectedProfiles?.findIndex(profile => profile.internalId === assert.validateProfileId);
                                    const selectedProfile = configs.selectedProfiles?.[index];
                                    return selectedProfile ? `${index} | ${selectedProfile.name}` : assert.validateProfileId;
                                })()}
                            </Form.Text>
                        )}
                    </div>
                );
            case 'headerField':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.headerfield')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                            </Form.Label>
                            {!isEditing && assert.headerField !== '' && (
                                <div>
                                    <Form.Text>{assert.headerField}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={headerField}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={headerField}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="headerField"
                                        value={assert.headerField}
                                        onChange={(e) => handleChange(e, assert)}
                                    />
                                </InputGroup>
                                {displayMessage('headerField')}
                            </>
                        )}
                    </div>
                );
            case 'valueToCompare':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.valuetocompare')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                            </Form.Label>
                            {!isEditing && assert.valueToCompare !== '' && (
                                <div>
                                    <Form.Text>{assert.valueToCompare}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={valueToCompare}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={valueToCompare}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="valueToCompare"
                                        value={assert.valueToCompare}
                                        onChange={(e) => handleChange(e, assert)}
                                    />
                                </InputGroup>
                                {displayMessage('valueToCompare')}
                            </>
                        )}
                    </div>
                );
            case 'operator':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.operator')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <Form.Select
                                    required={fieldRequired}
                                    name="operator"
                                    value={assert.operator}
                                    onChange={(e) => handleChange(e, assert)}
                                >
                                    <option
                                        className="text-muted"
                                        value=""
                                    >
                                        {i18n.t('placeholder.chooseoperator')}
                                    </option>
                                    {operatorOptions.map((operator, index) => (
                                        <option
                                            key={index}
                                            value={operator.code}
                                        >
                                            {operator.display}
                                        </option>
                                    ))}
                                </Form.Select>
                                {displayMessage('operator')}
                            </>
                        ) : (
                            <Form.Text>{assert.operator}</Form.Text>
                        )}
                    </div >
                );
            case 'requestMethod':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.requestmethod')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <Form.Select
                                    required={fieldRequired}
                                    name="requestMethod"
                                    value={assert.requestMethod}
                                    onChange={(e) => handleChange(e, assert)}
                                >
                                    <option
                                        className="text-muted"
                                        value=""
                                    >
                                        {i18n.t('placeholder.chooserequestmethod')}
                                    </option>
                                    {requestMethodOptions.map((requestMethod, index) => (
                                        <option
                                            key={index}
                                            value={requestMethod.code}
                                        >
                                            {requestMethod.display}
                                        </option>
                                    ))}
                                </Form.Select>
                                {displayMessage('requestMethod')}
                            </>
                        ) : (
                            <Form.Text>{assert.requestMethod}</Form.Text>
                        )}
                    </div>
                );
            case 'requestUrl':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.requesturl')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="requestUrl"
                                        value={assert.requestUrl}
                                        onChange={(e) => handleChange(e, assert)}
                                    />
                                </InputGroup>
                                {displayMessage('requestUrl')}
                            </>
                        ) : (
                            <Form.Text>{assert.requestUrl}</Form.Text>
                        )}
                    </div>
                );
            case 'contentType':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.contenttype')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                            </Form.Label>
                            {!isEditing && assert.contentType !== '' && (
                                <div>
                                    <Form.Text>{assert.contentType}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={contentType}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={contentType}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="contentType"
                                        value={assert.contentType}
                                        onChange={(e) => handleChange(e, assert)}
                                    />
                                </InputGroup>
                                {displayMessage('contentType')}
                            </>
                        )}
                    </div>
                );
            case 'resource':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.resource')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="resource"
                                        value={assert.resource}
                                        onChange={(e) => handleChange(e, assert)}
                                    />
                                </InputGroup>
                                {displayMessage('resource')}
                            </>
                        ) : (
                            <Form.Text>{assert.resource}</Form.Text>
                        )}
                    </div>
                );
            case 'sourceId':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.resourceforfhirpathverification')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                            </Form.Label>
                            {!isEditing && assert.sourceId !== '' && (
                                <div>
                                    <Form.Text>
                                        {configs.fixtures.find(fixture => fixture.internalId === assert.sourceId)?.id ?? assert.sourceId}
                                    </Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={sourceId}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={sourceId}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <Form.Select
                                    required={fieldRequired}
                                    name="sourceId"
                                    value={assert.sourceId}
                                    onChange={(e) => handleChange(e, assert)}
                                >
                                    <option
                                        className="text-muted"
                                        value=""
                                    >
                                        {i18n.t('placeholder.choosefixture')}
                                    </option>
                                    {configs.fixtures.map(fixture => (
                                        <option
                                            key={fixture.internalId}
                                            value={fixture.internalId}
                                        >
                                            {fixture.id}
                                        </option>
                                    ))}
                                </Form.Select>
                                {displayMessage('sourceId')}
                            </>
                        )}
                    </div>
                );
            case 'stopTestOnFail':
                return (
                    <div className="md-flex-container d-flex gap-2">
                        {isEditing ? (
                            <Form.Check
                                name="stopTestOnFail"
                                className="fw-bold"
                                type="switch"
                                label={i18n.t('label.stoptestonfail')}
                                defaultChecked={false}
                                checked={assert.stopTestOnFail}
                                onChange={(e) => handleSwitchChange(e, assert)}
                            />
                        ) : (
                            <Form.Check
                                name="stopTestOnFail"
                                className="fw-bold"
                                type="switch"
                                label={i18n.t('label.stoptestonfail')}
                                defaultChecked={false}
                                checked={assert.stopTestOnFail}
                                onChange={(e) => handleSwitchChange(e, assert)}
                            />
                        )}
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            title={stopTestOnFail}
                            className="mt-1 actionIcon"
                        />
                    </div>
                );
            case 'warningOnly':
                return (
                    <div className="md-flex-container d-flex gap-2">
                        {isEditing ? (
                            <Form.Check
                                name="warningOnly"
                                className="fw-bold"
                                type="switch"
                                label={i18n.t('label.warningonly')}
                                defaultChecked={false}
                                checked={assert.warningOnly}
                                onChange={(e) => handleSwitchChange(e, assert)}
                            />
                        ) : (
                            <Form.Check
                                name="warningOnly"
                                className="fw-bold"
                                type="switch"
                                label={i18n.t('label.warningonly')}
                                defaultChecked={false}
                                checked={assert.warningOnly}
                                onChange={(e) => handleSwitchChange(e, assert)}
                            />
                        )}
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            title={warningOnly}
                            className="mt-1 actionIcon"
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    /**
     * Handle the change of the switch input field
     *
     * @param e
     *
     */
    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>, assert: any) => {
        const { name } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prevFormData => ({
            ...prevFormData,
            [name]: checked
        }));
    };

    /**
     * Reinitialize the form data when an assert is added
     *
     */
    const reinitializeFormData = () => {
        setEditionValidated(false);
        setFormData({
            id: generateAssertId(configs.testStep, asserts.length + 1),
            description: '',
            label: '',
            direction: '',
            expression: '',
            responseCode: '',
            validateProfileId: '',
            headerField: '',
            valueToCompare: '',
            operator: '',
            requestMethod: '',
            requestUrl: '',
            contentType: '',
            resource: '',
            sourceId: '',
            stopTestOnFail: false,
            warningOnly: false,
        });
    };

    /**
     * Add a new assert to the list of asserts
     *
     */
    const submitAssertForm = () => {
        const newAssert = {
            ...formData,
            id: currentAssertId !== null ? currentAssertId : generateAssertId(configs.testStep, asserts.length + 1),
        };
        if (!validateAssertFields(newAssert)) {
            setEditionValidated(true);
            return;
        }
        if (currentAssertId !== null) {
            const updatedAsserts = asserts.map((assert: any) => (assert.id === currentAssertId ? newAssert : assert));
            configs.setAssertFormData((prevFormData: any) => ({
                ...prevFormData,
                [configs.operationId]: updatedAsserts
            }));
        } else {
            configs.setAssertFormData((prevFormData: any) => ({
                ...prevFormData,
                [configs.operationId]: [...asserts, newAssert]
            }));
        }
        reinitializeFormData();
        setShowForm(false);
        setCurrentAssertId(null);
    };

    /**
     * Access to a new form to fill the fields of the new assert
     * 
     */
    const displayAssertForm = () => {
        setShowForm(true);
        const assertCount = asserts ? asserts.length : 0;
        const newAssertId = generateAssertId(configs.testStep, assertCount + 1);
        setFormData(prevFormData => ({
            ...prevFormData,
            id: newAssertId,
        }));
    };

    /**
     * Cancel the assert form
     * 
     */
    const cancelAssertForm = () => {
        reinitializeFormData();
        setShowForm(false);
        setCurrentAssertId(null);
    };

    /**
     * Handle the cancel of an assert and display a confirmation message
     * 
     */
    const handleCancelAssert = () => {
        if (window.confirm(i18n.t('confirmationmessage.cancelassert'))) {
            cancelAssertForm();
        }
    };

    /////////////////////////////
    //         Content         //
    /////////////////////////////

    return (
        <>
            {/* ACCORDION SECTION WHEN AN ASSERT WAS ADDED */}
            <Accordion
                className="mb-2"
            >
                {asserts && asserts.length > 0 && asserts.map((assert: any, index: any) => (
                    assert.id && currentAssertId !== assert.id &&
                    <Accordion.Item
                        key={index}
                        eventKey="0"
                    >
                        <Accordion.Header>
                            <div className="d-flex w-100 justify-content-between me-3">
                                <Title
                                    level={3}
                                    content={assert.id}
                                >
                                </Title>
                                <div className="d-flex flex-wrap mt-3 gap-2">
                                    <FontAwesomeIcon
                                        icon={faPen}
                                        className="actionIcon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAssertEditingClick(assert);
                                        }}
                                    />
                                    <FontAwesomeIcon
                                        icon={faTrash}
                                        className="actionIcon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteAssert(assert.id);
                                        }}
                                    />
                                </div>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="d-flex flex-column gap-4">
                                <div className="d-flex flex-wrap w-100 gap-4">
                                    <div className="md-flex-container">
                                        <Form.Label className="fw-bold">
                                            Description :&nbsp;
                                        </Form.Label>
                                        <Form.Text>{assert.description}</Form.Text>
                                    </div>
                                    <div className="md-flex-container">
                                        <Form.Label className="fw-bold">
                                            Label :&nbsp;
                                        </Form.Label>
                                        <Form.Text>{assert.label}</Form.Text>
                                    </div>
                                    {assert.label && (
                                        assertFieldsByLabel[assert.label as keyof typeof assertFieldsByLabel]?.map((field) =>
                                            (assert[field as keyof typeof formData] || field === 'warningOnly' || field === 'stopTestOnFail') ? renderField(field, assert, false) : null
                                        ))}
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>

            {/* SECTION TO ADD OR EDIT AN ASSERT */}
            {showForm && (
                <Accordion
                    defaultActiveKey="0"
                    alwaysOpen
                    className="mb-1"
                >
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <Title
                                level={3}
                                content={formData.id}
                            >
                            </Title>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className={`d-flex flex-wrap gap-4 ${editionValidated ? 'was-validated' : ''}`}>
                                <div className="d-flex flex-wrap w-100 gap-4">
                                    <div className="w-100">
                                        <Form.Label className="fw-bold">
                                            Description *
                                        </Form.Label>
                                        <Form.Control
                                            required
                                            name="description"
                                            as="textarea"
                                            rows={5}
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="form-control"
                                        />
                                        {displayMessage('description')}
                                    </div>
                                    <div className="w-100">
                                        <Form.Label className="fw-bold">
                                            Label *
                                        </Form.Label>
                                        <Form.Select
                                            required
                                            name="label"
                                            value={formData.label}
                                            onChange={handleChange}
                                        >
                                            <option
                                                className='text-muted'
                                                value=""
                                            >
                                                {i18n.t('placeholder.chooselabel')}
                                            </option>
                                            {labelOptions.map((option, index) => (
                                                <option
                                                    key={index}
                                                    value={option.code}
                                                >
                                                    {option.display}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {displayMessage('label')}
                                    </div>
                                    {formData.label && (
                                        assertFieldsByLabel[formData.label as keyof typeof assertFieldsByLabel]?.map((field) =>
                                            renderField(field, formData, true))
                                    )}
                                </div>
                                <div className="d-flex gap-4">
                                    <Button
                                        type="button"
                                        className="w-9rem"
                                        variant="primary"
                                        onClick={submitAssertForm}
                                    >
                                        {i18n.t('button.add')}
                                    </Button>
                                    <Button
                                        className="w-9rem"
                                        role="button"
                                        variant="secondary"
                                        onClick={handleCancelAssert}
                                    >
                                        {i18n.t('button.cancel')}
                                    </Button>
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}

            {!showForm && (
                <div
                    className="actionIcon d-flex flex-wrap align-items-center gap-2 w-9rem"
                    onClick={displayAssertForm}
                >
                    <FontAwesomeIcon
                        icon={faPlus}
                        role="button"
                    />
                    <strong
                        role="button"
                    >
                        {i18n.t('general.addassert')}
                    </strong>
                </div>
            )}
        </>
    );
};

export default AddTestScriptAssert;